import * as yup from 'yup';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CreateAccount,
  DoNotHaveAccount,
  EmailAddress,
  ForgotPassword,
  Href,
  Password,
  RememberPassword,
  SignIn,
  SignInAccount,
  SignInWith,
} from '../constants';
import { useAppDispatch } from '../reduxToolkit/hooks';
import { login } from '../reduxToolkit/reducers/layout/authSlice';
import SocialLink from './SocialLink';
import { routes } from '../routers/routes';
import { loginUser } from '../services/api';
import Loader from '../layout/Loader';
import { capture, initAnalytics } from '../services/analytics';
import { UserRole } from '../types/Forms';

// Yup is a schema builder for runtime value parsing and validation
const schema = yup.object().shape({
  email: yup.string().email('Enter a valid email address').required('Email is required'),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters long')
    .required('Password is required'),
});

//Login form
const SignInForm = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const toggle = () => setPasswordVisible(!isPasswordVisible);
  const [showLoader, setShowLoader] = useState(false);
  // useForm is a custom hook for managing forms with ease, with yupResolver to validate the form schema
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { email: '', password: '' },
  });
  const { t } = useTranslation();
  // form submission handler
  const onSubmit = async ({ email, password }: { email: string; password: string }) => {
    try {
      setShowLoader(true);
      const loginData = await loginUser({ email, password });
      if (loginData.login) {
        toast.success('Login Success...!');
        dispatch(login(loginData));
        if (loginData.user.role === 'student') {
          await initAnalytics({
            dest: 'https',
            endpoint: `${process.env.REACT_APP_BACKEND_API}/analytics/events`,
            userRole: loginData.user.role ?? null,
            user_id: loginData.user.id,
          });
          capture('auth_login', { method: 'with_password' });
        }
      } else {
        toast.error('Please Enter a valid email or password...!');
      }
    } catch (error) {
      console.log('loginUser error', error);
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      {showLoader && <Loader />}
      <Form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
        <h3>{t('auth.signInToAccount')}</h3>
        <p>{t('auth.enterCredentials')}</p>
        <FormGroup>
          <Label className="col-form-label">{t('auth.labelEmailAddress')}</Label>
          <input
            className="form-control"
            type="email"
            placeholder={t('auth.emailAddressPlaceholder')}
            {...register('email')}
          />
          {errors.email && <p className="text-danger mt-1">{errors.email.message}</p>}
        </FormGroup>
        <FormGroup>
          <Label className="col-form-label">{t('auth.labelPassword')}</Label>
          <div className="form-input position-relative">
            <input
              className="form-control"
              placeholder={t('auth.passwordPlaceholder')}
              type={isPasswordVisible ? 'text' : 'password'}
              {...register('password')}
            />
            <div className="show-hide" onClick={toggle}>
              <span>
                {isPasswordVisible ? t('auth.inputHidePassword') : t('auth.inputShowPassword')}{' '}
              </span>
            </div>
          </div>
          {errors.password && <p className="text-danger mt-1">{errors.password.message}</p>}
        </FormGroup>
        <div className="form-group mb-0">
          {/* <div className="checkbox p-0">
          <Input id="checkbox1" type="checkbox" />
          <Label className="text-muted" htmlFor="checkbox1">
            {RememberPassword}
          </Label>
        </div> */}
          <Link className="link" to={Href}>
            {ForgotPassword}
          </Link>
          <div className="text-end mt-3">
            <Button color="primary" className="w-100" block>
              {t('auth.signIn')}
            </Button>
          </div>
        </div>
        {/* <h6 className="text-muted mt-4 or">{SignInWith}</h6> */}
        {/* <SocialLink /> */}
        {/* <p className="mt-4 mb-0 text-center">
        {DoNotHaveAccount}
        <Link className="ms-2" to={Href}>
          {CreateAccount}
        </Link>
      </p> */}
      </Form>
    </>
  );
};

export default SignInForm;
